import { useEffect, useRef, useState } from "react";
import axios from "axios";
const API = process.env.REACT_APP_BACKEND_URL || "";
const EMPTY = { text: "", outcome: "", support_received_percent: null, preparation: null };
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// One writer per mounted question. A server version protects other tabs/devices.
export default function usePupilLearningDraft(item) {
  const key = item?.draft?.token || (item ? `${item.id || item.check_id}:${item.question_step || 0}:${item.follow_up_task_id || ""}` : null);
  const active = useRef(null);
  const [, render] = useState(0);
  const mounted = useRef(true);
  if (active.current?.key !== key) {
    active.current = { key, token: item?.draft?.token, version: item?.draft?.version || 0,
      values: { ...EMPTY, ...item?.draft?.values }, dirty: false, status: item?.draft?.saved_at ? "saved" : "", retired: false };
  }
  const session = active.current;
  const update = () => { if (mounted.current && active.current === session) render(n => n + 1); };
  const flush = async () => {
    clearTimeout(session.timer);
    if (session.pending) return session.pending;
    if (!session.token || !session.dirty || session.retired) return true;
    session.pending = (async () => {
      while (session.dirty && !session.retired) {
        const values = session.values;
        const version = session.version;
        session.status = "saving"; update();
        try {
          const { data } = await axios.put(`${API}/api/learner-framework/me/learning-queue/draft`,
            { token: session.token, version, values }, { withCredentials: true });
          if (data?.token !== session.token || data?.submitted !== false || !Number.isInteger(data.version) || data.version < Math.max(1, version) ||
              !equal(data.values, values) || !data.saved_at) throw new Error("Draft save unconfirmed");
          session.version = data.version;
          session.dirty = !equal(session.values, values);
          session.status = "saved"; update();
        } catch {
          session.status = "error"; update(); return false;
        }
      }
      return true;
    })();
    try { return await session.pending; } finally { session.pending = null; }
  };
  const edit = (patch) => {
    session.values = { ...session.values, ...patch };
    session.dirty = true; session.status = session.token ? "saving" : "";
    clearTimeout(session.timer);
    session.timer = setTimeout(flush, 500);
    update();
  };
  const retire = () => {
    session.retired = true; clearTimeout(session.timer);
    if (session.token) axios.delete(`${API}/api/learner-framework/me/learning-queue/draft/${session.token}`, { withCredentials: true }).catch(() => {});
  };
  useEffect(() => {
    mounted.current = true;
    const hidden = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", hidden);
    return () => { clearTimeout(session.timer); document.removeEventListener("visibilitychange", hidden); };
    // A session is scoped to one exact question, not to each edit/render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => () => { mounted.current = false; }, []);
  return { values: session.values, status: session.status, edit, flush, retire };
}
